// EmojiPicker.tsx - The whole emoji set (emojibase), searchable, with recents and skin tones. The data loads the first
// time the picker opens, and emojis this computer can't draw (newer than its emoji font) are left out.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../assets/styles/EmojiPicker.css';

interface EmojiPickerProps {
  onEmojiSelect: (emoji: string) => void;
  disabled?: boolean;
}

interface Skin { hexcode: string; unicode: string }
interface Emoji { label: string; hexcode: string; unicode: string; group: number; order: number; tags: string[]; skins: Skin[] }

const GROUPS: Array<{ id: number; icon: string; name: string }> = [
  { id: 0, icon: '😀', name: 'Smileys & emotion' },
  { id: 1, icon: '👋', name: 'People & body' },
  { id: 3, icon: '🐻', name: 'Animals & nature' },
  { id: 4, icon: '🍔', name: 'Food & drink' },
  { id: 5, icon: '✈️', name: 'Travel & places' },
  { id: 6, icon: '⚽', name: 'Activities' },
  { id: 7, icon: '💡', name: 'Objects' },
  { id: 8, icon: '❤️', name: 'Symbols' },
  { id: 9, icon: '🏁', name: 'Flags' }
];
const RECENT = -1;
const TONES = ['', '1F3FB', '1F3FC', '1F3FD', '1F3FE', '1F3FF'];
const TONE_SWATCH = ['✋', '✋🏻', '✋🏼', '✋🏽', '✋🏾', '✋🏿'];
const RECENT_KEY = 'emcog.emoji.recent';
const TONE_KEY = 'emcog.emoji.tone';
const RECENT_MAX = 32;

const stored = <T,>(key: string, fallback: T): T => {
  try { const v = localStorage.getItem(key); return v ? JSON.parse(v) as T : fallback; } catch { return fallback; }
};
const store = (key: string, value: unknown) => { try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* private window */ } };

/**
 * Can this computer draw it as one emoji? Newer emojis come out as an empty box (no colour) or, for joined sequences,
 * as their parts side by side (too wide); a country flag on Windows is two plain letters. Only the newest versions and
 * the flags are checked — older ones are everywhere.
 */
function drawable(): (unicode: string) => boolean {
  const canvas = document.createElement('canvas');
  canvas.width = 64; canvas.height = 32;
  const ctx = canvas.getContext('2d', { willReadFrequently: true } as CanvasRenderingContext2DSettings);
  if (!ctx) return () => true;
  ctx.font = '24px "Segoe UI Emoji","Apple Color Emoji","Noto Color Emoji",sans-serif';
  ctx.textBaseline = 'top';
  const one = ctx.measureText('😀').width;
  const draw = (text: string): Uint8ClampedArray => { ctx.clearRect(0, 0, 64, 32); ctx.fillText(text, 0, 0); return ctx.getImageData(0, 0, 32, 32).data; };
  const same = (a: Uint8ClampedArray, b: Uint8ClampedArray) => a.every((v, i) => v === b[i]);
  return (unicode: string) => {
    if (ctx.measureText(unicode).width > one * 1.4) return false;
    const px = draw(unicode);
    let colour = false;
    for (let i = 0; i < px.length && !colour; i += 4) {
      if (px[i + 3] > 0 && (Math.abs(px[i] - px[i + 1]) > 24 || Math.abs(px[i + 1] - px[i + 2]) > 24)) colour = true;
    }
    if (!colour) return false;
    // A sequence drawn as just its first piece (England's flag as a plain black flag) isn't the emoji.
    const first = String.fromCodePoint(unicode.codePointAt(0) || 0);
    return first === unicode || !same(px, draw(first));
  };
}

let loading: Promise<Emoji[]> | null = null;
/** The emoji set, once per page: emojibase's English data, minus components and what can't be drawn here. */
function loadEmojis(): Promise<Emoji[]> {
  if (!loading) {
    loading = Promise.all([import('emojibase-data/en/compact.json'), import('emojibase-data/versions/emoji.json')]).then(([data, versions]) => {
      const all = ((data as any).default || data) as any[];
      const byVersion = ((versions as any).default || versions) as Record<string, string[]>;
      const recent = new Set(Object.entries(byVersion).filter(([v]) => parseFloat(v) >= 14).flatMap(([, codes]) => codes));
      const canDraw = drawable();
      return all
        .filter(e => typeof e.group === 'number' && e.group !== 2 && e.unicode)
        // Country flags too: Windows has never drawn them (they come out as two letters).
        .filter(e => !(recent.has(e.hexcode) || e.group === 9) || canDraw(e.unicode))
        .map(e => ({ label: e.label, hexcode: e.hexcode, unicode: e.unicode, group: e.group, order: e.order ?? 0, tags: e.tags || [], skins: e.skins || [] }))
        .sort((a, b) => a.order - b.order);
    });
    loading.catch(() => { loading = null; });
  }
  return loading;
}

/** An emoji in the chosen skin tone (single-tone variants only; the default when it has none). */
function inTone(e: Emoji, tone: number): string {
  if (!tone || !e.skins.length) return e.unicode;
  const want = TONES[tone];
  const hit = e.skins.find(s => {
    const tones = s.hexcode.split('-').filter(c => TONES.includes(c) && c);
    return tones.length > 0 && tones.every(c => c === want);
  });
  return hit ? hit.unicode : e.unicode;
}

const EmojiPicker: React.FC<EmojiPickerProps> = ({ onEmojiSelect, disabled }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [emojis, setEmojis] = useState<Emoji[] | null>(null);
  const [failed, setFailed] = useState(false);
  const [query, setQuery] = useState('');
  const [recent, setRecent] = useState<string[]>(() => stored<string[]>(RECENT_KEY, []));
  const [group, setGroup] = useState<number>(() => (stored<string[]>(RECENT_KEY, []).length ? RECENT : 0));
  const [tone, setTone] = useState<number>(() => stored<number>(TONE_KEY, 0));
  const [hover, setHover] = useState<string>('');
  const pickerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!isOpen) return;
    setFailed(false);
    loadEmojis().then(setEmojis).catch(() => setFailed(true));
    searchRef.current?.focus();
    const outside = (event: MouseEvent) => {
      if (!pickerRef.current?.contains(event.target as Node) && !buttonRef.current?.contains(event.target as Node)) setIsOpen(false);
    };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape') setIsOpen(false); };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('mousedown', outside); document.removeEventListener('keydown', escape); };
  }, [isOpen]);

  const byUnicode = useMemo(() => {
    const m = new Map<string, Emoji>();
    for (const e of emojis || []) { m.set(e.unicode, e); for (const s of e.skins) m.set(s.unicode, e); }
    return m;
  }, [emojis]);

  const shown = useMemo((): Array<{ e: Emoji; char: string }> => {
    if (!emojis) return [];
    const q = query.trim().toLowerCase();
    if (q) {
      const words = q.split(/\s+/);
      // Its name first ("party" → 🥳 partying face before 😛, whose keywords include party), then its keywords.
      const rank = (e: Emoji): number => {
        const name = e.label.toLowerCase();
        if (words.every(w => name.split(/[\s:-]+/).some(n => n.startsWith(w)))) return 0;
        if (words.every(w => name.includes(w))) return 1;
        return words.every(w => name.includes(w) || e.tags.some(t => t.startsWith(w))) ? 2 : -1;
      };
      return emojis
        .map(e => ({ e, r: rank(e) }))
        .filter(x => x.r >= 0)
        .sort((a, b) => a.r - b.r || a.e.order - b.e.order)
        .slice(0, 240).map(({ e }) => ({ e, char: inTone(e, tone) }));
    }
    if (group === RECENT) return recent.map(char => ({ e: byUnicode.get(char)!, char })).filter(x => x.e);
    return emojis.filter(e => e.group === group).map(e => ({ e, char: inTone(e, tone) }));
  }, [emojis, query, group, recent, tone, byUnicode]);

  const pick = useCallback((char: string) => {
    onEmojiSelect(char);
    const next = [char, ...recent.filter(r => r !== char)].slice(0, RECENT_MAX);
    setRecent(next);
    store(RECENT_KEY, next);
  }, [onEmojiSelect, recent]);

  const chooseGroup = (id: number) => { setGroup(id); setQuery(''); gridRef.current?.scrollTo({ top: 0 }); };
  const chooseTone = (t: number) => { setTone(t); store(TONE_KEY, t); };
  const title = query.trim() ? `${shown.length ? shown.length : 'No'} match${shown.length === 1 ? '' : 'es'}` : group === RECENT ? 'Recently used' : GROUPS.find(g => g.id === group)?.name;

  return (
    <div className="emoji-picker-container">
      <button
        ref={buttonRef}
        className={`emoji-picker-button ${isOpen ? 'active' : ''}`}
        onClick={() => { if (!disabled) setIsOpen(!isOpen); }}
        disabled={disabled}
        title="Add emoji"
        type="button"
        aria-label="Open emoji picker"
        aria-expanded={isOpen}
      >
        😊
      </button>

      {isOpen && (
        <div ref={pickerRef} className="emoji-picker-popup" role="dialog" aria-label="Emoji">
          <input
            ref={searchRef}
            className="emoji-search"
            type="search"
            placeholder="Search emoji"
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && shown[0]) { e.preventDefault(); pick(shown[0].char); } }}
            aria-label="Search emoji"
          />
          <div className="emoji-category-tabs" role="tablist">
            {recent.length > 0 && (
              <button className={`emoji-category-tab ${!query && group === RECENT ? 'active' : ''}`} onClick={() => chooseGroup(RECENT)} title="Recently used" type="button" role="tab" aria-selected={!query && group === RECENT}>🕘</button>
            )}
            {GROUPS.map(g => (
              <button key={g.id} className={`emoji-category-tab ${!query && group === g.id ? 'active' : ''}`} onClick={() => chooseGroup(g.id)} title={g.name} type="button" role="tab" aria-selected={!query && group === g.id}>{g.icon}</button>
            ))}
          </div>
          <div className="emoji-category-label">{title}</div>
          <div ref={gridRef} className="emoji-grid" onMouseLeave={() => setHover('')}>
            {!emojis && <div className="emoji-empty">{failed ? 'Emoji couldn’t load — try again.' : 'Loading…'}</div>}
            {shown.map(({ e, char }) => (
              <button key={char} className="emoji-item" onClick={() => pick(char)} onMouseEnter={() => setHover(e.label)} onFocus={() => setHover(e.label)} title={e.label} aria-label={e.label} type="button">
                {char}
              </button>
            ))}
          </div>
          <div className="emoji-footer">
            <span className="emoji-hover-label">{hover || ' '}</span>
            <span className="emoji-tones" role="radiogroup" aria-label="Skin tone">
              {TONE_SWATCH.map((s, t) => (
                <button key={t} className={`emoji-tone ${tone === t ? 'active' : ''}`} onClick={() => chooseTone(t)} title={t ? `Skin tone ${t}` : 'Default skin tone'} aria-label={t ? `Skin tone ${t}` : 'Default skin tone'} role="radio" aria-checked={tone === t} type="button">{s}</button>
              ))}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmojiPicker;
